import React, { useEffect, useState } from "react";
import { ThreeRow } from "../../core/layouts";
import { Text, Tag } from "react_reusable_components";
import CustomInput, {
  customInputStyle,
} from "../../components/CustomInput/CustomInput";
import variables from "../../themes/base/styles/variables";
import { getLocalizedMessage } from "../../UtilityFunctions/utils";
import { useIntl } from "react-intl";
import { addUserFormFieldNames } from "../../validationSchema/addUserManagementSchema";
import { EMAIL_REGEX } from "../../constants";
import DNSDAddressContainer from "../DNSDAddressContainer";

const AdditionEmailContainerStyle = {
  tagStyle: {
    borderRadius: variables.radiusXXLarge,
    lineHeight: variables.lineHeightNormal,
    fontWeight: variables.fontWeightNormal,
    color: variables.textDefault,
    padding: `${variables.marginXSmallest} ${variables.marginSmallest} ${variables.marginXSmallest} ${variables.marginMediumSmall}`,
    height: variables.tagHeight,
    display: variables.dFlex,
    alignItems: variables.alignCenter,
    justifyContent: variables.spaceBetween,
    gap: variables.gapXS,
    border: variables.tagBorder,
  },
  headerStyle: {
    fontSize: variables.fontSizeNormal,
    lineHeight: variables.lineHeightNormal,
    color: variables.textDefault,
  },
  topSectionStyle: {
    marginBottom: variables.marginSmall,
    marginTop: variables.marginMedium,
  },
  dnsdLabelStyle: {
    fontSize: variables.fontSizeSmall,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightNormal,
    color: variables.textDefault,
    marginTop: variables.marginMedium,
  },
  dnsdInputStyle: {
    fontSize: variables.fontSizeNormal,
    lineHeight: variables.lineHeightSmall,
    color: variables.textDefault,
    border: `1px solid ${variables.grey4}`,
    padding: `${variables.paddingSmall2} ${variables.paddingSmall} ${variables.paddingXXXXLarge} ${variables.paddingSmall}`,
  },
  bottomSectionStyle: {
    marginTop: variables.marginSmallest,
  },
  additionalEmailInputStyle: {
    width: variables.unset,
    flex: 1,
    border: "none",
    fontSize: variables.fontSizeNormal,
    minWidth: variables.otpInputHeight,
    padding: variables.unset,
    height: variables.unset,
  },
  middleSectionStyle: {
    flexWrap: variables.flexWrap,
    gap: variables.gapXS,
    position: "relative",
    alignItems: variables.alignCenter,
    paddingBottom: variables.paddingMedium,
  },
  crossIconStyle: {
    width: variables.smallIconWidth,
    height: variables.smallIconHeight,
    color: variables.iconColor,
  },
  additionalEmailsWrapperStyle: {
    all: "inherit",
    width: variables.fullWidth,
    border: `1px solid ${variables.grey4}`,
    padding: `${variables.paddingSmall2} ${variables.paddingXSmall}`,
    position: variables.unset,
    paddingBottom: variables.paddingSmall2,
  },
  emailErrorStyle: {
    bottom: "0rem",
    marginTop: variables.marginMedium2,
  },
  fullWidth: {
    width: variables.fullWidth,
  },
};

export default function AdditionEmailContainer({
  additionalEmails,
  setAdditionalEmails,
  values,
  errors,
  handleChange,
  additionalEmailError,
  setAdditionalEmailError,
  touched,
}) {
  const intl = useIntl();
  const regex = new RegExp(EMAIL_REGEX);
  const [inputEmail, setInputEmail] = useState("");
  const removeEmailHandler = (e, value) => {
    const newEmailsArray = additionalEmails.filter(
      (email) => email.value !== value
    );
    setAdditionalEmails(newEmailsArray);
  };

  const getAdditionEmails = (emails) => {
    return (
      <>
        {emails?.map((email) => (
          <Tag
            key={email}
            closeIcon={
              <img
                src="/icons/close.svg"
                alt="Close Icon"
                style={AdditionEmailContainerStyle.crossIconStyle}
              />
            }
            color={variables.lightCraftingBlue}
            closable={true}
            onClose={(e) => removeEmailHandler(e, email.value)}
            style={AdditionEmailContainerStyle.tagStyle}
          >
            {email?.value}
          </Tag>
        ))}
      </>
    );
  };

  const checkEmailAlreadyExist = (email) => {
    if (email === values[addUserFormFieldNames.EMAIL]) {
      setAdditionalEmailError(
        getLocalizedMessage(intl, "addUser.exist.additionalEmail")
      );
      return true;
    }
    if (additionalEmails?.some((emailObj) => emailObj.value === email)) {
      setAdditionalEmailError(
        getLocalizedMessage(intl, "addUser.exist.additionalEmail")
      );
      return true;
    } else {
      setAdditionalEmailError("");
      return false;
    }
  };
  
  const isInvalidEmail = (emailValue) => {
    if (!regex.test(emailValue)) {
      setAdditionalEmailError(
        getLocalizedMessage(intl, "addUser.invalid.text.additionalEmail")
      );
      return true;
    }
  };
  const addEmailHandler = (e) => {
    if (isInvalidEmail(inputEmail)) {
      return;
    }
    if (checkEmailAlreadyExist(inputEmail)) {
      return;
    } else {
      setAdditionalEmails((prev) => [...prev, { value: inputEmail }]);
      setInputEmail("");
    }
  };

  useEffect(() => {
    if (inputEmail === "") {
      setAdditionalEmailError("");
    }
    if (
      additionalEmails
        .map(({ value }) => value)
        .includes(values[addUserFormFieldNames.EMAIL])
    ) {
      setAdditionalEmailError(
        getLocalizedMessage(intl, "addUser.exist.additionalEmailSame")
      );
    }
  }, [additionalEmails, inputEmail, intl, setAdditionalEmailError, values]);

  return (
    <ThreeRow
      topSection={
        <Text style={AdditionEmailContainerStyle.headerStyle}>
          {getLocalizedMessage(intl, "additionEmail.text")}
        </Text>
      }
      topSectionStyle={AdditionEmailContainerStyle.topSectionStyle}
      middleSectionStyle={AdditionEmailContainerStyle.middleSectionStyle}
      middleSection={
        <>
          <div style={AdditionEmailContainerStyle.additionalEmailsWrapperStyle}>
            {getAdditionEmails(additionalEmails)}
            <CustomInput
              style={AdditionEmailContainerStyle.additionalEmailInputStyle}
              onChange={(e) => setInputEmail(e.target.value)}
              onPressEnter={addEmailHandler}
              value={inputEmail}
              size="small"
            />
          </div>
          {!!additionalEmailError && (
            <Text
              style={{
                ...customInputStyle.errorStyle,
                ...AdditionEmailContainerStyle.emailErrorStyle,
              }}
              type="danger"
            >
              {additionalEmailError}
            </Text>
          )}
        </>
      }
      bottomSection={
        <DNSDAddressContainer
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
        />
      }
    />
  );
}
